import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import useAccess from "../../hooks/useAccess";
import SectionCard from "../../components/SectionCard";

const DEFAULT_PATTERN = "[count|5]";
const DEFAULT_START = "1";
const DEFAULT_CURRENT = "0";

const EmployeeNumberConfigSection = () => {
  const [pattern, setPattern] = useState(DEFAULT_PATTERN);
  const [startStr, setStartStr] = useState(DEFAULT_START);
  const [currentStr, setCurrentStr] = useState(DEFAULT_CURRENT);
  const [exampleFromStart, setExampleFromStart] = useState("");
  const [exampleNextIfGenerateNow, setExampleNextIfGenerateNow] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // 🧪 State: Access & Auth
  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    employee_numbers: {
      edit: hasPermission("AccountSetting", "Profile", "edit"),
    },
  };

  const BASE_URL = process.env.REACT_APP_API_BASE_URL || "";
  const token = localStorage.getItem("token");
  const authHeader = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const initialized = useRef(false);

  const padWithPattern = useCallback((patt, numStr) => {
    const pad = parseInt(patt.match(/\[count\|(\d+)\]/)?.[1] || "5", 10);
    const digitsOnly = String(numStr ?? "0").replace(/[^\d]/g, "") || "0";
    return digitsOnly.padStart(pad, "0");
  }, []);

  const recalcExamples = useCallback(
    (patt, startValStr, currentValStr) => {
      setExampleFromStart(padWithPattern(patt, startValStr || "0"));
      const next = String((parseInt(currentValStr || "0", 10) || 0) + 1);
      setExampleNextIfGenerateNow(padWithPattern(patt, next));
    },
    [padWithPattern]
  );

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        setLoading(true);
        setMessage("");
        const res = await axios.get(`${BASE_URL}/api/employee-number-config`, {
          headers: authHeader,
        });

        const data = res.data || {
          en_pattern: DEFAULT_PATTERN,
          en_starting_value: Number(DEFAULT_START),
          en_current_value: Number(DEFAULT_CURRENT),
        };

        if (!initialized.current) {
          const patt = data.en_pattern ?? DEFAULT_PATTERN;
          const start = String(data.en_starting_value ?? 1);
          const curr = String(data.en_current_value ?? 0);
          setPattern(patt);
          setStartStr(start);
          setCurrentStr(curr);
          recalcExamples(patt, start, curr);
          initialized.current = true;
        }
      } catch (err) {
        console.error("Failed to fetch config", err);
        setMessage("❌ Unable to load configuration (check login).");
      } finally {
        setLoading(false);
      }
    };

    fetchConfig();
  }, [BASE_URL, authHeader, recalcExamples]);

  const onPatternChange = (e) => {
    const value = e.target.value;
    setPattern(value);
    recalcExamples(value, startStr, currentStr);
  };

  const onStartChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, "");
    setStartStr(value);

    // 🔥 Auto-update current to start - 1
    const autoCurr = String(Math.max(Number(value) - 1, 0));
    setCurrentStr(autoCurr);

    recalcExamples(pattern, value, autoCurr);
  };

  const onCurrentChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, "");
    setCurrentStr(value);
    recalcExamples(pattern, startStr, value);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage("");

      if (!/^\d+$/.test(startStr)) {
        setMessage("❌ Starting Value must be a non-negative integer.");
        return;
      }
      if (!/^\d+$/.test(currentStr)) {
        setMessage("❌ Current Value must be a non-negative integer.");
        return;
      }

      const startNum = Number(startStr);
      const currentNum = Number(currentStr);

      await axios.post(
        `${BASE_URL}/api/employee-number-config`,
        {
          en_pattern: pattern || DEFAULT_PATTERN,
          en_starting_value: startNum,
          en_current_value: currentNum,
        },
        { headers: authHeader }
      );

      setMessage("✔️ Configuration updated successfully!");
    } catch (err) {
      console.error("Failed to save config", err);
      setMessage("❌ Error saving configuration.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="account-settings-tab-panel account-settings-profile-tab">
      <div className="account-settings-tab-header">
        <div>
          <h2>Employee Number Creation</h2>
          <p>Configure the numbering sequence used when employee IDs are generated.</p>
        </div>
      </div>
      <SectionCard>
      <div style={{ marginBottom: "1rem" }}>
        <strong>Available Tokens</strong>
        <ul className="mb-3 mt-2 small text-muted">
          <li>
            <code>[count|number]</code> – Auto-incremented padded number
          </li>
        </ul>
      </div>

      <div className="mb-3">
        <label>
          <strong>Pattern:</strong>
        </label>
        <input
          type="text"
          className="form-control"
          value={pattern}
          onChange={onPatternChange}
          placeholder="[count|5]"
          disabled
        />
      </div>

      <div className="mb-3">
        <label>
          <strong>Starting Value:</strong>
        </label>
        <input
          type="text"
          className="form-control"
          value={startStr}
          onChange={onStartChange}
          inputMode="numeric"
          placeholder="e.g., 20001"
          disabled={!PERMISSIONS.employee_numbers.edit}
        />
      </div>

      <div className="mb-3">
        <label>
          <strong>Current Value (last issued):</strong>
        </label>
        <input
          type="text"
          className="form-control"
          value={currentStr}
          onChange={onCurrentChange}
          inputMode="numeric"
          placeholder="e.g., 20001"
          disabled={!PERMISSIONS.employee_numbers.edit}
        />
        <small className="text-muted">
          This is the last number that has been used. The next generated will be{" "}
          <b>{exampleNextIfGenerateNow}</b>.
        </small>
      </div>

      <div className="mb-3">
        <div>
          <strong>Example from Starting Value:</strong> {exampleFromStart}
        </div>
        <div>
          <strong>Next if Generate Now:</strong> {exampleNextIfGenerateNow}
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={loading || !PERMISSIONS.employee_numbers.edit}
        className="btn btn-primary"
      >
        {loading ? "Saving..." : "Save Configuration"}
      </button>

      {message && <div className="mt-3">{message}</div>}
      </SectionCard>
    </div>
  );
};

export default EmployeeNumberConfigSection;
